/**
 * ArtifactTagBar — the file-tag chip group for the project chat composer.
 *
 * Carries forward D9's correct ARIA (`role="group"` + `aria-pressed` toggles) and replaces its
 * plain buttons with chips that read as a selection, not as five actions.
 *
 * ── CONTRAST, DELIBERATELY ─────────────────────────────────────────────────────────────────────
 * Track 3 §5.2 suggests selected chips use `bg-primary-subtle + text-primary-700 + ring-1
 * ring-primary`. `text-primary-700` did not exist as a utility — only raw `--color-primary`, which
 * measures ~4.1:1 on `--color-primary-subtle` and fails AA for normal text. Rather than ship a
 * failing pairing or invent a colour, `--color-primary-700` (already in tokens.css) was promoted
 * into `@theme`; the pairing now measures ~6.1:1. The ring and the check icon mean selection is
 * never conveyed by colour alone.
 */

import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  ARTIFACT_LABELS,
  ARTIFACT_TYPES,
  type ArtifactType,
} from "@/hooks/useArtifacts";

export interface ArtifactTagBarProps {
  selected: ArtifactType[];
  onToggle: (artifactType: ArtifactType) => void;
  disabled?: boolean;
  /**
   * Tags that were applied automatically because the chat was opened from inside that file
   * (CR Decision 1). Rendered with a hint so the user understands why it is pre-ticked — and it
   * stays removable, which the decision requires explicitly.
   */
  autoTagged?: ArtifactType[];
  /**
   * Which artifacts actually exist, and therefore which chips can succeed (§4.4, AD-8).
   *
   * THREE-VALUED on purpose:
   *   • `undefined` → "not yet known" → **enable all five** (FAIL OPEN)
   *   • `[]`        → "none exist"    → disable all five
   *   • `[a, b]`    → only those two are enabled
   *
   * `undefined` and `[]` must stay distinct. This consumer is deliberately the OPPOSITE of
   * `ChatSystemPrompt` and `SourcesSheet`, which fail CLOSED while the artifact queries are
   * unresolved. The asymmetry is justified by the asymmetry in blast radius: tagging a genuinely
   * missing artifact hits `postTurn`'s `404 ARTIFACT_NOT_FOUND` backstop — a recoverable toast —
   * whereas a wrongly-offered Generate button costs the user's drafts. Disabled-then-enabled is
   * also a flash that can swallow a click the user already aimed at an enabled-looking chip.
   *
   * Unavailable chips stay VISIBLE rather than hidden, so the set of five is stable and the user can
   * see what exists.
   */
  available?: readonly ArtifactType[];
  /**
   * Rendered after the chips, inside this row's `role="group"`, pushed right with `ml-auto`.
   *
   * A SLOT rather than an `onOpenSources` callback (Item 7): this bar's concern is layout, and a
   * slot keeps it from importing the Sources panel's semantics. The row already uses
   * `flex flex-wrap items-center gap-2`, so on a narrow viewport the trailing content wraps to its
   * own line rather than compressing the chips.
   */
  trailing?: React.ReactNode;
  className?: string;
}

export function ArtifactTagBar({
  selected,
  onToggle,
  disabled = false,
  autoTagged = [],
  available,
  trailing,
  className,
}: ArtifactTagBarProps): JSX.Element {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      role="group"
      aria-label="Files this instruction applies to"
    >
      {ARTIFACT_TYPES.map((artifactType) => {
        const isSelected = selected.includes(artifactType);
        const isAuto = isSelected && autoTagged.includes(artifactType);
        const label = ARTIFACT_LABELS[artifactType];
        // `undefined` means "not yet known" → every chip is available. See the prop's doc for why
        // this consumer fails OPEN while the others fail closed.
        const isAvailable = available === undefined || available.includes(artifactType);
        const isDisabled = disabled || !isAvailable;

        return (
          <button
            key={artifactType}
            type="button"
            aria-pressed={isSelected}
            disabled={isDisabled}
            onClick={() => onToggle(artifactType)}
            title={
              !isAvailable
                ? 'Not generated yet — generate the Phase 0 artifacts first.'
                : isAuto
                  ? `${label} — tagged automatically because it is open. Click to remove.`
                  : undefined
            }
            className={cn(
              "inline-flex items-center gap-1.5 rounded-badge border border-outlines px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
              isSelected
                ? "border-primary bg-primary-subtle text-primary-700 ring-1 ring-primary"
                : "border-outlines bg-white text-text-secondary hover:border-text-placeholder hover:text-text-primary",
            )}
          >
            {isSelected && (
              <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
            )}
            {label}
            {isAuto && (
              <span className="text-[10px] font-normal opacity-80">· open</span>
            )}
          </button>
        );
      })}
      {trailing}
    </div>
  );
}

export default ArtifactTagBar;
